import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Modal, Form, Button, Row, Col, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import SelectedMetersModal from '../AlertRules/SelectedMetersModal';
import { useUsers } from '../hooks/useUsers';
import { createReportRule, updateReportRule } from '../services/reportRuleService';
import '../styles/ReportRules.css';

// ─── Cron helpers ────────────────────────────────────────────────────────────

/**
 * Build a weekly cron expression from day-of-week (0–6) and HH:MM time string.
 * Result format: "minute hour * * dayOfWeek"
 */
const buildCron = (dayOfWeek, time) => {
  const [h, m] = (time || '09:00').split(':');
  return `${parseInt(m, 10)} ${parseInt(h, 10)} * * ${dayOfWeek}`;
};

/**
 * Parse a weekly cron expression back into { dayOfWeek, time }.
 * Falls back to sensible defaults if parsing fails.
 */
const parseCron = (cron) => {
  if (!cron) return { dayOfWeek: 1, time: '09:00' };
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return { dayOfWeek: 1, time: '09:00' };
  const [minute, hour, , , dow] = parts;
  const h = String(hour).padStart(2, '0');
  const m = String(minute).padStart(2, '0');
  return { dayOfWeek: parseInt(dow, 10), time: `${h}:${m}` };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const REPORT_TYPES = [
  { value: 'METER_ACTVITY_SUMMARY', label: 'Meter Activity Summary' },
];

const DEFAULT_FORM = {
  name: '',
  reportType: 'METER_ACTVITY_SUMMARY',
  sourceIds: [],
  subLocationIds: [],
  module: '',
  categories: [],
  subCategories: [],
  kpis: [],
  meterIds: [],           // intentionally empty — only populated on explicit selection
  isEnabled: true,
  dayOfWeek: 1,           // Monday
  time: '09:00',
  recipientEmails: [],
  recipientCc: [],
};

// ─── Component ───────────────────────────────────────────────────────────────

const ReportRuleForm = forwardRef(({ onSave, meters, sources, currentFinancialYear }, ref) => {
  const [show, setShow] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSelectedMeters, setShowSelectedMeters] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [validation, setValidation] = useState({});

  const { data: users = [] } = useUsers();

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.email,
        label: `${u.first_name} ${u.last_name}`.trim() || u.email,
      })),
    [users],
  );

  // ── Derived filter options ────────────────────────────────────────────────

  const availableSubLocations = useMemo(() => {
    if (formData.sourceIds.length === 0) return [];
    const selectedSources = sources.filter((s) => formData.sourceIds.includes(s.value));
    const subLocs = selectedSources.flatMap((s) => s.subLocations || []);
    return Array.from(new Map(subLocs.map((sl) => [sl.value, sl])).values());
  }, [formData.sourceIds, sources]);

  const filterMeters = (base) => {
    let result = base;
    if (formData.sourceIds.length > 0)
      result = result.filter((m) => formData.sourceIds.includes(m.sourceId));
    if (formData.subLocationIds.length > 0)
      result = result.filter((m) => formData.subLocationIds.includes(m.subLocationId));
    return result;
  };

  const getKpi = (m) =>
    m.financialYearKpis?.find((f) => f.financialYear === currentFinancialYear)?.kpi;

  const availableModules = useMemo(() => {
    const base = filterMeters(meters);
    return [...new Set(base.map((m) => getKpi(m)?.module).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [formData.sourceIds, formData.subLocationIds, meters, currentFinancialYear]);

  const availableCategories = useMemo(() => {
    let base = filterMeters(meters);
    if (formData.module)
      base = base.filter((m) => getKpi(m)?.module === formData.module);
    return [...new Set(base.map((m) => getKpi(m)?.category).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, meters, currentFinancialYear]);

  const availableSubCategories = useMemo(() => {
    let base = filterMeters(meters);
    if (formData.module) base = base.filter((m) => getKpi(m)?.module === formData.module);
    if (formData.categories.length > 0)
      base = base.filter((m) => formData.categories.includes(getKpi(m)?.category));
    return [...new Set(base.map((m) => getKpi(m)?.subCategory).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, formData.categories, meters, currentFinancialYear]);

  const availableKpis = useMemo(() => {
    let base = filterMeters(meters);
    if (formData.module) base = base.filter((m) => getKpi(m)?.module === formData.module);
    if (formData.categories.length > 0)
      base = base.filter((m) => formData.categories.includes(getKpi(m)?.category));
    if (formData.subCategories.length > 0)
      base = base.filter((m) => formData.subCategories.includes(getKpi(m)?.subCategory));
    return [...new Set(base.map((m) => getKpi(m)?.kpi).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, formData.categories, formData.subCategories, meters, currentFinancialYear]);

  /**
   * The meter list shown in the select reflects the current hierarchy filters.
   * meterIds are only set when the user explicitly picks from this list.
   */
  const availableMeterIds = useMemo(() => {
    let base = filterMeters(meters);
    if (formData.module) base = base.filter((m) => getKpi(m)?.module === formData.module);
    if (formData.categories.length > 0)
      base = base.filter((m) => formData.categories.includes(getKpi(m)?.category));
    if (formData.subCategories.length > 0)
      base = base.filter((m) => formData.subCategories.includes(getKpi(m)?.subCategory));
    if (formData.kpis.length > 0)
      base = base.filter((m) => formData.kpis.includes(getKpi(m)?.kpi));

    const map = new Map();
    base.forEach((m) => {
      if (m.meterId && !map.has(m.meterId)) map.set(m.meterId, m.meterName || m.meterId);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, formData.categories, formData.subCategories, formData.kpis, meters, currentFinancialYear]);

  const selectedMetersData = useMemo(
    () => meters.filter((m) => formData.meterIds.includes(m.meterId)),
    [meters, formData.meterIds],
  );

  // ── Imperative handle ─────────────────────────────────────────────────────

  useImperativeHandle(ref, () => ({
    show: (rule = null) => {
      if (rule) {
        setEditingRule(rule);
        const { dayOfWeek, time } = parseCron(rule.cronExpression);
        setFormData({
          name: rule.name || '',
          reportType: rule.reportType || 'METER_ACTVITY_SUMMARY',
          sourceIds: rule.criteria?.sourceId || [],
          subLocationIds: [],
          module: rule.criteria?.module || '',
          categories: rule.criteria?.category || [],
          subCategories: rule.criteria?.subCategory || [],
          kpis: rule.criteria?.kpi || [],
          meterIds: rule.criteria?.meterId || [],
          isEnabled: rule.isEnabled ?? true,
          dayOfWeek,
          time,
          recipientEmails: rule.recipients?.emails || [],
          recipientCc: rule.recipients?.cc || [],
        });
      } else {
        setEditingRule(null);
        setFormData(DEFAULT_FORM);
      }
      setValidation({});
      setError(null);
      setShow(true);
    },
  }));

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleClose = () => {
    setShow(false);
    setEditingRule(null);
    setError(null);
    setValidation({});
  };

  const clearValidation = (field) => {
    if (validation[field]) {
      setValidation((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  /**
   * Generic field change. Downstream fields are reset when a parent filter changes
   * (same cascade logic as AlertRuleForm), but meterIds are only ever updated
   * through the explicit meter selector.
   */
  const handleChange = (field, value) => {
    clearValidation(field);

    setFormData((prev) => {
      switch (field) {
        case 'sourceIds':
          return { ...prev, sourceIds: value, subLocationIds: [], module: '', categories: [], subCategories: [], kpis: [], meterIds: [] };
        case 'subLocationIds':
          return { ...prev, subLocationIds: value, module: '', categories: [], subCategories: [], kpis: [], meterIds: [] };
        case 'module':
          return { ...prev, module: value, categories: [], subCategories: [], kpis: [], meterIds: [] };
        case 'categories':
          return { ...prev, categories: value, subCategories: [], kpis: [], meterIds: [] };
        case 'subCategories':
          return { ...prev, subCategories: value, kpis: [], meterIds: [] };
        case 'kpis':
          return { ...prev, kpis: value, meterIds: [] };
        default:
          return { ...prev, [field]: value };
      }
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Report name is required';
    if (!formData.module) errors.module = 'Module is required';
    if (!formData.dayOfWeek && formData.dayOfWeek !== 0)
      errors.dayOfWeek = 'Day of week is required';
    if (!formData.time) errors.time = 'Time is required';

    if (formData.recipientEmails.length === 0) {
      errors.recipientEmails = 'At least one recipient email is required';
    } else {
      const invalid = formData.recipientEmails.filter((e) => !isValidEmail(e));
      if (invalid.length > 0)
        errors.recipientEmails = `Invalid email format: ${invalid.join(', ')}`;
    }

    if (formData.recipientCc.length > 0) {
      const invalid = formData.recipientCc.filter((e) => !isValidEmail(e));
      if (invalid.length > 0)
        errors.recipientCc = `Invalid CC email format: ${invalid.join(', ')}`;
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const cronExpression = buildCron(formData.dayOfWeek, formData.time);

      const payload = {
        userId: 1, // TODO: replace with auth context
        name: formData.name,
        reportType: formData.reportType,
        cronExpression,
        criteria: {
          sourceId: formData.sourceIds,
          module: formData.module,
          category: formData.categories,
          subCategory: formData.subCategories,
          kpi: formData.kpis,
          // Only include meterIds if the user explicitly selected some;
          // an empty array means "all meters matching the hierarchy above"
          meterId: formData.meterIds,
        },
        recipients: {
          emails: formData.recipientEmails,
          cc: formData.recipientCc,
        },
        isEnabled: formData.isEnabled,
      };

      if (editingRule) {
        await updateReportRule(editingRule.id, payload);
      } else {
        await createReportRule(payload);
      }

      handleClose();
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save report rule');
    } finally {
      setLoading(false);
    }
  };

  // ── Cadence preview ───────────────────────────────────────────────────────

  const cronPreview = useMemo(() => {
    if (formData.dayOfWeek === '' || !formData.time) return null;
    const day = DAYS_OF_WEEK.find((d) => d.value === formData.dayOfWeek)?.label ?? '';
    return `Runs every ${day} at ${formData.time}  →  cron: ${buildCron(formData.dayOfWeek, formData.time)}`;
  }, [formData.dayOfWeek, formData.time]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRule ? 'Edit Report Rule' : 'Add Report Rule'}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="alert-rule-form-body">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* ── Basic Information ─────────────────────────────────── */}
            <div className="form-section mb-4">
              <h6 className="form-section-title">Basic Information</h6>

              <Form.Group className="mb-3">
                <Form.Label>Report Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter report name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  isInvalid={!!validation.name}
                />
                <Form.Control.Feedback type="invalid">{validation.name}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Report Type *</Form.Label>
                <Form.Select
                  value={formData.reportType}
                  onChange={(e) => handleChange('reportType', e.target.value)}
                >
                  {REPORT_TYPES.map((rt) => (
                    <option key={rt.value} value={rt.value}>{rt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Enable Report Rule"
                  checked={formData.isEnabled}
                  onChange={(e) => handleChange('isEnabled', e.target.checked)}
                />
              </Form.Group>
            </div>

            {/* ── Cadence ───────────────────────────────────────────── */}
            <div className="form-section mb-4">
              <h6 className="form-section-title">Schedule (Weekly)</h6>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Day of Week *</Form.Label>
                    <Select
                      options={DAYS_OF_WEEK}
                      value={DAYS_OF_WEEK.find((d) => d.value === formData.dayOfWeek) ?? null}
                      onChange={(selected) => handleChange('dayOfWeek', selected?.value ?? '')}
                      placeholder="Select day..."
                      isClearable
                      className={validation.dayOfWeek ? 'is-invalid' : ''}
                    />
                    {validation.dayOfWeek && (
                      <div className="invalid-feedback d-block">{validation.dayOfWeek}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Time of Day *</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleChange('time', e.target.value)}
                      isInvalid={!!validation.time}
                    />
                    <Form.Control.Feedback type="invalid">{validation.time}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {cronPreview && (
                <div className="p-2 rounded bg-light border">
                  <small className="text-muted font-monospace">{cronPreview}</small>
                </div>
              )}
            </div>

            {/* ── Criteria ──────────────────────────────────────────── */}
            <div className="form-section mb-4">
              <h6 className="form-section-title">Report Criteria</h6>
              <Form.Text className="text-muted d-block mb-3">
                The report will include all meters matching the selected hierarchy. Leaving a
                level blank means "all" at that level. Meter IDs are optional — if left empty,
                all meters matching the criteria above will be included automatically.
              </Form.Text>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Select
                      isMulti
                      options={sources}
                      value={sources.filter((s) => formData.sourceIds.includes(s.value))}
                      onChange={(sel) => handleChange('sourceIds', sel.map((s) => s.value))}
                      placeholder="All locations (leave blank for all)"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sub Location</Form.Label>
                    <Select
                      isMulti
                      options={availableSubLocations}
                      value={availableSubLocations.filter((sl) => formData.subLocationIds.includes(sl.value))}
                      onChange={(sel) => handleChange('subLocationIds', sel.map((sl) => sl.value))}
                      placeholder="All sub locations (leave blank for all)"
                      isDisabled={availableSubLocations.length === 0}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Module *</Form.Label>
                    <Select
                      options={availableModules}
                      value={availableModules.find((m) => m.value === formData.module) ?? null}
                      onChange={(sel) => handleChange('module', sel?.value || '')}
                      placeholder="Select module..."
                      isDisabled={availableModules.length === 0}
                      isClearable
                      className={validation.module ? 'is-invalid' : ''}
                    />
                    {validation.module && (
                      <div className="invalid-feedback d-block">{validation.module}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Select
                      isMulti
                      options={availableCategories}
                      value={availableCategories.filter((c) => formData.categories.includes(c.value))}
                      onChange={(sel) => handleChange('categories', sel.map((c) => c.value))}
                      placeholder="All categories (leave blank for all)"
                      isDisabled={availableCategories.length === 0}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sub Category</Form.Label>
                    <Select
                      isMulti
                      options={availableSubCategories}
                      value={availableSubCategories.filter((sc) => formData.subCategories.includes(sc.value))}
                      onChange={(sel) => handleChange('subCategories', sel.map((sc) => sc.value))}
                      placeholder="All sub categories (leave blank for all)"
                      isDisabled={availableSubCategories.length === 0}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>KPI</Form.Label>
                    <Select
                      isMulti
                      options={availableKpis}
                      value={availableKpis.filter((k) => formData.kpis.includes(k.value))}
                      onChange={(sel) => handleChange('kpis', sel.map((k) => k.value))}
                      placeholder="All KPIs (leave blank for all)"
                      isDisabled={availableKpis.length === 0}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Meter IDs — explicit selection only, no auto-population */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Meter IDs{' '}
                  <span className="text-muted fw-normal">(optional — leave blank to include all matching meters)</span>
                </Form.Label>
                <Select
                  isMulti
                  options={availableMeterIds}
                  value={availableMeterIds.filter((m) => formData.meterIds.includes(m.value))}
                  onChange={(sel) => handleChange('meterIds', sel.map((m) => m.value))}
                  placeholder="Leave blank for all matching meters..."
                  isDisabled={availableMeterIds.length === 0}
                  className={validation.meterIds ? 'is-invalid' : ''}
                />
                {validation.meterIds && (
                  <div className="invalid-feedback d-block">{validation.meterIds}</div>
                )}
                {formData.meterIds.length > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2 p-0"
                    onClick={() => setShowSelectedMeters(true)}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-1" />
                    Show Selected Meters ({formData.meterIds.length})
                  </Button>
                )}
              </Form.Group>
            </div>

            {/* ── Recipients ────────────────────────────────────────── */}
            <div className="form-section mb-3">
              <h6 className="form-section-title">Recipients</h6>

              <Form.Group className="mb-3">
                <Form.Label>To (Email Recipients) *</Form.Label>
                <CreatableSelect
                  isMulti
                  options={userOptions}
                  value={formData.recipientEmails.map((email) => {
                    const found = userOptions.find((u) => u.value === email);
                    return found || { value: email, label: email };
                  })}
                  onChange={(sel) =>
                    handleChange('recipientEmails', sel ? sel.map((s) => s.value) : [])
                  }
                  placeholder="Select or type email addresses..."
                  formatCreateLabel={(v) => `Add email: ${v}`}
                  isValidNewOption={(v) => isValidEmail(v)}
                  className={validation.recipientEmails ? 'is-invalid' : ''}
                />
                {validation.recipientEmails && (
                  <div className="invalid-feedback d-block">{validation.recipientEmails}</div>
                )}
                <Form.Text className="text-muted">
                  Select from list or type an email address and press Enter
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>CC</Form.Label>
                <CreatableSelect
                  isMulti
                  options={userOptions}
                  value={(formData.recipientCc || []).map((email) => {
                    const found = userOptions.find((u) => u.value === email);
                    return found || { value: email, label: email };
                  })}
                  onChange={(sel) =>
                    handleChange('recipientCc', sel ? sel.map((s) => s.value) : [])
                  }
                  placeholder="Select or type CC email addresses..."
                  formatCreateLabel={(v) => `Add email: ${v}`}
                  isValidNewOption={(v) => isValidEmail(v)}
                  className={validation.recipientCc ? 'is-invalid' : ''}
                />
                {validation.recipientCc && (
                  <div className="invalid-feedback d-block">{validation.recipientCc}</div>
                )}
                <Form.Text className="text-muted">
                  Select from list or type an email address and press Enter
                </Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Saving...' : editingRule ? 'Update Report Rule' : 'Create Report Rule'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <SelectedMetersModal
        show={showSelectedMeters}
        onHide={() => setShowSelectedMeters(false)}
        meters={selectedMetersData}
      />
    </>
  );
});

export default ReportRuleForm;